import React from "react";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import CrmButton from "./CrmButton";
import CrmCard from "./CrmCard";

/**
 * A linear wizard (Import: "Five steps, one screen each, Back/Next throughout").
 *
 * The step chip reads "1 · Upload" (wireframe). The caller owns the state; this renders
 * the progress row, the step body and the Back / Next pair. `canNext` false disables
 * Next, and `nextHint` says why (spec: "Next disabled until email is mapped"). The last
 * step supplies its own primary action, so Next is hidden there.
 *
 * `steps`: `[{key, label}]`.
 */
export default function Wizard({ steps, index, children, onBack, onNext, canNext = true, busy = false, nextHint, ids = {} }) {
  const t = useT("crm");
  const last = index === steps.length - 1;
  return (
    <div className="space-y-4">
      <ol className="flex flex-wrap gap-2" aria-label={t("wizard.progress")}>
        {steps.map((s, i) => (
          <li key={s.key} aria-current={i === index ? "step" : undefined}
            className={cn(
              "rounded-full px-3 py-1 text-xs",
              i === index ? "bg-eh-ink text-eh-bg" : i < index ? "bg-eh-sage/30 text-eh-ink" : "bg-white border border-eh-border-soft text-eh-muted",
            )}>
            {t("wizard.stepChip", { n: i + 1, label: s.label })}
          </li>
        ))}
      </ol>
      <CrmCard tone="raised" data-testid={ids.step?.(index + 1)}>
        <p className="mb-4 text-xs uppercase tracking-[0.18em] text-eh-muted">
          {t("wizard.stepChip", { n: index + 1, label: steps[index].label })}
        </p>
        {children}
      </CrmCard>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CrmButton variant="outline" onClick={onBack} disabled={index === 0 || busy} data-testid={ids.back}>{t("wizard.back")}</CrmButton>
        {!last && (
          <div className="flex flex-wrap items-center gap-3">
            {!canNext && nextHint && <span className="text-xs text-eh-muted">{nextHint}</span>}
            <CrmButton onClick={onNext} disabled={!canNext || busy} data-testid={ids.next}>{t("wizard.next")}</CrmButton>
          </div>
        )}
      </div>
    </div>
  );
}
